/**
 * Typed client for Permits & NOC (prepare-to-attest).
 *
 * Routes (beam-backend permitsRouter.ts, /api, Bearer; reads open to any role,
 * mutations owner/office):
 *   GET  /api/jurisdictions
 *   GET  /api/permit-types
 *   GET  /api/contractor-licenses
 *   GET  /api/contractor-licenses/expiring
 *   GET  /api/permit-applications
 *   POST /api/permit-applications/prepare            { permit_type_id, job_id?, ... } (prefill ONLY)
 *   POST /api/permit-applications/:id/mark-submitted (records a HUMAN submitted it elsewhere)
 *   POST /api/permit-applications/:id/status         { status }
 *   GET  /api/permit-applications/:id/inspections
 *   POST /api/permit-applications/:id/inspections    { ... }
 *   POST /api/inspections/:id/result                 { result }
 *   GET  /api/nocs
 *   POST /api/nocs/generate                          { job_id, ... } (PDF draft)
 *   POST /api/nocs/:id/mark-signed                   (records a HUMAN signed/notarized it)
 *   POST /api/nocs/:id/mark-recorded                 (records a HUMAN recorded it at the registry)
 *   GET  /api/jobs/:jobId/noc-compliance
 *
 * LEGAL / SAFETY: this module is prepare-to-attest. Nothing here auto-submits,
 * auto-notarizes, auto-records or files anything. `prepare`/`generate` only build
 * a draft; the mark-* mutations RECORD that a human performed the legal action.
 *
 * New endpoints return raw Prisma rows (snake_case); typed locally rather than
 * adding a zod DTO per shape. Bearer token via client. NEVER imports @smartbox/*.
 */
import { get, post } from './client';

// ---------------------------------------------------------------------------
// Row shapes (raw snake_case from the backend)
// ---------------------------------------------------------------------------

export interface JurisdictionRow {
  id: string;
  name: string;
  level?: string | null;
  portal_url?: string | null;
  portal_integration?: string | null;
  automation_method?: string | null;
  // legacy/optional fields some callers may reference
  state?: string | null;
  county?: string | null;
}

export interface PermitTypeRow {
  id: string;
  jurisdiction_id: string | null;
  name: string;
  code: string | null;
  description?: string | null;
  fee_basis?: string | null;
  base_fee?: string | null;
  noc_required_threshold?: string | null;
}

export interface ContractorLicenseRow {
  id: string;
  license_number: string;
  classification: string | null;
  jurisdiction_id: string | null;
  status: string;
  expires_at: string | null;
}

export interface PermitApplicationRow {
  id: string;
  permit_type_id: string | null;
  job_id: string | null;
  jurisdiction_id: string | null;
  permit_number: string | null;
  status: string;
  applicant_name: string | null;
  project_address: string | null;
  scope_of_work: string | null;
  fee_amount: string | null;
  submitted_at: string | null;
  created_at: string;
}

export interface InspectionRow {
  id: string;
  permit_application_id: string;
  inspection_type: string | null;
  status: string | null;
  result: string | null;
  scheduled_at: string | null;
  completed_at: string | null;
  notes: string | null;
}

export interface NocRow {
  id: string;
  job_id: string | null;
  status: string;
  document_url: string | null;
  legal_warning: string | null;
  owner_name: string | null;
  property_address: string | null;
  signed_at: string | null;
  recorded_at: string | null;
  recording_reference: string | null;
  created_at: string;
}

export interface NocComplianceRow {
  job_id: string;
  required: boolean;
  status: string;
  noc_id: string | null;
  message: string | null;
}

// ---------------------------------------------------------------------------
// Request bodies
// ---------------------------------------------------------------------------

export interface PreparePermitApplicationRequest {
  permit_type_id: string;
  job_id?: string;
  applicant_name?: string;
  project_address?: string;
  scope_of_work?: string;
  [key: string]: unknown;
}

export interface RecordInspectionRequest {
  inspection_type?: string;
  scheduled_at?: string;
  notes?: string;
  [key: string]: unknown;
}

export interface GenerateNocRequest {
  job_id: string;
  owner_name?: string;
  property_address?: string;
  [key: string]: unknown;
}

export interface PrepareNocRequest {
  owner_name: string;
  owner_address?: string;
  property_legal_description?: string;
  general_description?: string;
  tax_folio_number?: string;
  owner_interest?: string;
  contractor_name?: string;
  contractor_address?: string;
  contract_value?: number;
  permit_number?: string;
  work_type?: 'hvac' | 'other' | 'unknown';
}

/** Result of generating a NOC (job-coupled or manual). */
export interface GeneratedNoc {
  noc: NocRow;
  attachment_id: string;
  pdf_url: string;
  nocRequired: { required: boolean; reason: string; needsHumanReview: boolean };
}

// ---------------------------------------------------------------------------
// Reference data
// ---------------------------------------------------------------------------

/** List jurisdictions (reference). */
export async function listJurisdictions(): Promise<JurisdictionRow[]> {
  return get<JurisdictionRow[]>('/api/jurisdictions');
}

/** List permit types (reference; used to choose what to prepare). */
export async function listPermitTypes(): Promise<PermitTypeRow[]> {
  return get<PermitTypeRow[]>('/api/permit-types');
}

export interface CreateJurisdictionRequest {
  name: string;
  level: 'city' | 'county' | 'state';
  portal_url?: string;
  portal_integration?: 'energov' | 'munis' | 'opengov' | 'manual';
  automation_method?: 'api' | 'form_fill' | 'manual';
}

/** Create a jurisdiction (owner/office). The "permit path" data the rest of the feature reads. */
export async function createJurisdiction(body: CreateJurisdictionRequest): Promise<JurisdictionRow> {
  return post<JurisdictionRow>('/api/jurisdictions', body);
}

export interface CreatePermitTypeRequest {
  jurisdiction_id: string;
  name: string;
  code?: string;
  fee_basis?: string;
  base_fee?: number;
  noc_required_threshold?: number;
}

/** Create a permit type under a jurisdiction (owner/office). */
export async function createPermitType(body: CreatePermitTypeRequest): Promise<PermitTypeRow> {
  return post<PermitTypeRow>('/api/permit-types', body);
}

/** List contractor licenses. */
export async function listContractorLicenses(): Promise<ContractorLicenseRow[]> {
  return get<ContractorLicenseRow[]>('/api/contractor-licenses');
}

/** List contractor licenses expiring soon (renewal reminders). */
export async function listExpiringLicenses(): Promise<ContractorLicenseRow[]> {
  return get<ContractorLicenseRow[]>('/api/contractor-licenses/expiring');
}

// ---------------------------------------------------------------------------
// Permit applications
// ---------------------------------------------------------------------------

/** List permit applications. */
export async function listPermitApplications(): Promise<PermitApplicationRow[]> {
  return get<PermitApplicationRow[]>('/api/permit-applications');
}

/**
 * PREPARE (prefill) a permit application draft. Does NOT submit, file or attest —
 * it only builds the draft for the owner to review and submit themselves.
 */
export async function preparePermitApplication(
  body: PreparePermitApplicationRequest,
): Promise<PermitApplicationRow> {
  return post<PermitApplicationRow>('/api/permit-applications/prepare', body);
}

/**
 * RECORD that a human already submitted this application in the permit portal.
 * BEAM does not submit — this only tracks that the owner did it elsewhere.
 */
export async function markPermitSubmitted(id: string): Promise<PermitApplicationRow> {
  return post<PermitApplicationRow>(`/api/permit-applications/${id}/mark-submitted`);
}

/** Update the tracked status of a permit application. */
export async function setPermitStatus(id: string, status: string): Promise<PermitApplicationRow> {
  return post<PermitApplicationRow>(`/api/permit-applications/${id}/status`, { status });
}

/** List inspections for a permit application. */
export async function listInspections(appId: string): Promise<InspectionRow[]> {
  return get<InspectionRow[]>(`/api/permit-applications/${appId}/inspections`);
}

/** Record the RESULT a human/inspector reported for an inspection. */
export async function recordInspectionResult(inspId: string, result: string): Promise<InspectionRow> {
  return post<InspectionRow>(`/api/inspections/${inspId}/result`, { result });
}

// ---------------------------------------------------------------------------
// Notice of Commencement (NOC)
// ---------------------------------------------------------------------------

/** List NOCs. */
export async function listNocs(): Promise<NocRow[]> {
  return get<NocRow[]>('/api/nocs');
}

/**
 * PREPARE a NOC draft PDF from manually-entered facts, with no associated job.
 * For owners who have the property/owner details in hand (e.g. a permit pulled
 * outside a BEAM job). DRAFT only — BEAM does not file or notarize it.
 */
export async function prepareNoc(body: PrepareNocRequest): Promise<GeneratedNoc> {
  return post<GeneratedNoc>('/api/nocs/prepare', body);
}

/** RECORD that a human signed/notarized the NOC. BEAM does not notarize. */
export async function markNocSigned(id: string): Promise<NocRow> {
  return post<NocRow>(`/api/nocs/${id}/mark-signed`);
}

/** RECORD that a human recorded the NOC with the registry/clerk. BEAM does not record. */
export async function markNocRecorded(id: string): Promise<NocRow> {
  return post<NocRow>(`/api/nocs/${id}/mark-recorded`);
}

/** NOC compliance status for a job. */
export async function getNocCompliance(jobId: string): Promise<NocComplianceRow> {
  return get<NocComplianceRow>(`/api/jobs/${jobId}/noc-compliance`);
}

// ---------------------------------------------------------------------------
// Capability tiers (companion §1.2). Prepare-to-attest; tier fallback is never an error.
// Backend routes: beam-backend/src/api/permitsRouter.ts (capability-tier section).
// ---------------------------------------------------------------------------

export interface PermitEnvelopeRow {
  id: string;
  job_id: string;
  snapshot: { core?: Record<string, unknown>; provenance?: Record<string, unknown> } | null;
  work_classification: string | null;
  square_footage: string | null;
  trade_extension: Record<string, unknown> | null;
}

export interface PacketDocumentRow {
  id: string;
  doc_type: string;
  required: boolean;
  required_source: string;
  status: string;
  source: string;
  attachment_id: string | null;
}

export interface PacketRow {
  packet: { id: string; permit_application_id: string; rendered_attachment_id: string | null };
  documents: PacketDocumentRow[];
}

export interface PermitCorrectionRow {
  id: string;
  summary: string | null;
  status: string;
  source: string;
  requested_by_ahj_at: string | null;
  created_at: string;
}

export interface NewFilingRequest {
  jobId: string;
  jurisdictionId: string;
  permitTypeId: string;
  estimateId?: string;
  workClassification?: string;
  squareFootage?: number;
  floodZone?: string;
  qualifierName?: string;
  tradeExtension?: Record<string, unknown>;
}

/** List permit applications for a single job (Permit list screen). */
export async function listPermitsByJob(jobId: string): Promise<PermitApplicationRow[]> {
  return get<PermitApplicationRow[]>(`/api/permit-applications?jobId=${encodeURIComponent(jobId)}`);
}

/** Get a single permit application (detail screen). */
export async function getPermitApplication(id: string): Promise<PermitApplicationRow> {
  return get<PermitApplicationRow>(`/api/permit-applications/${id}`);
}

/** New filing: build the canonical envelope + create a draft application linked to it. */
export async function createEnvelope(body: NewFilingRequest): Promise<{ application: PermitApplicationRow; envelope: PermitEnvelopeRow }> {
  return post(`/api/permit-applications/new-filing`, body);
}

/** Build/refresh the Tier-0/1 packet manifest + PDF. */
export async function buildPacket(appId: string): Promise<{ packet: unknown; attachment_id: string; pdf_url: string; documents: number }> {
  return post(`/api/permit-applications/${appId}/packet`);
}

/** Get the packet manifest for an application. */
export async function getPacket(appId: string): Promise<PacketRow | null> {
  return get<PacketRow | null>(`/api/permit-applications/${appId}/packet`);
}

/** Upload a picked file (base64) and attach it to a packet slot in one call. */
export async function uploadPacketDocument(packetDocumentId: string, file: { fileName: string; mimeType: string; contentBase64: string; signedSealed?: boolean }): Promise<PacketDocumentRow> {
  return post<PacketDocumentRow>(`/api/permit-packet-documents/${packetDocumentId}/upload`, {
    fileName: file.fileName,
    mimeType: file.mimeType,
    contentBase64: file.contentBase64,
    signed_sealed: file.signedSealed ?? false,
  });
}

/** Tier-4 file-by-email handoff (assemble + send from the tenant identity). */
export async function fileByEmail(appId: string, cloudLinkUrl?: string): Promise<{ application: PermitApplicationRow; send: { status: string } }> {
  return post(`/api/permit-applications/${appId}/file/email`, { cloudLinkUrl });
}

/** Guarded lifecycle advance. */
export async function advancePermit(appId: string, status: string): Promise<PermitApplicationRow> {
  return post<PermitApplicationRow>(`/api/permit-applications/${appId}/advance`, { status });
}

/** Corrections for an application. */
export async function listCorrections(appId: string): Promise<PermitCorrectionRow[]> {
  return get<PermitCorrectionRow[]>(`/api/permit-applications/${appId}/corrections`);
}

/** Record off-platform fee payment (track-only). */
export async function recordFeePayment(appId: string, paymentMethod?: string): Promise<PermitApplicationRow> {
  return post<PermitApplicationRow>(`/api/permit-applications/${appId}/record-fee-payment`, { payment_method: paymentMethod });
}

export interface EligibilityResult {
  ok: boolean;
  warnings: { code: string; message: string; licenseId?: string }[];
  selectedLicenseId: string | null;
}

/** Warn-only license eligibility for a trade + jurisdiction. */
export async function checkEligibility(tradeId?: string, jurisdictionId?: string): Promise<EligibilityResult> {
  const q = new URLSearchParams();
  if (tradeId) q.set('tradeId', tradeId);
  if (jurisdictionId) q.set('jurisdictionId', jurisdictionId);
  return get<EligibilityResult>(`/api/license-eligibility?${q.toString()}`);
}

export interface TierSelection {
  capability_tier: number;
  filing_method: string;
}

/** Preview the automatically-selected filing tier for a jurisdiction (highest provisioned, else T0). */
export async function getFilingTier(jurisdictionId: string, opts?: { prefill?: boolean }): Promise<TierSelection> {
  const q = new URLSearchParams({ jurisdictionId });
  if (opts?.prefill) q.set('prefill', 'true');
  return get<TierSelection>(`/api/filing-tier?${q.toString()}`);
}

/**
 * Tier-3 assisted-browser: stage envelope values into the portal session for HUMAN attestation,
 * then the screen opens the returned handoffUrl. (Tiers 2 and 5 — AI form-fill and portal API
 * submit — are backend/operator concerns per the spec and are not exposed on the device.)
 */
export async function prepareAssistedSession(appId: string): Promise<{ vendor: string; handoffUrl: string; stagedValues: Record<string, unknown>; requiresHumanAttestation: true; humanChallenge?: string | null }> {
  return post(`/api/permit-applications/${appId}/assisted-session`);
}
